/**
 * Cliente HTTP de la pestaña Novedades. Vive al lado de la vista que lo usa
 * (§2.1) y solo llama a rutas que `back/src/novedades/routes.ts` expone.
 */

import type { TipoChofer } from '../../api';
import {
  pedir,
  postear,
  type RespuestaConsulta,
  type RespuestaImpacto,
  type RespuestaOperaciones,
} from '../../api';

/** Operaciones disponibles: las define el servidor, no una lista del front. */
export async function operaciones(): Promise<readonly string[]> {
  const r = await pedir<RespuestaOperaciones>('/api/novedades/operaciones');
  return r.operaciones;
}

/**
 * Qué se consulta: de qué choferes y de qué fechas.
 *
 * Cada población trae su propia ventana —el propio por mes y año, el eventual
 * por un intervalo— y pueden ir las dos juntas. De ahí que los campos del
 * período sean opcionales: viaja el que corresponda a lo elegido, y nunca uno
 * que no se usa.
 */
export interface SeleccionPeriodo {
  readonly tiposChofer: readonly TipoChofer[];
  /** Período de liquidación. Va cuando se consultan propios. */
  readonly anio?: number;
  readonly mes?: number;
  /** Intervalo trabajado. Va cuando se consultan eventuales. */
  readonly desde?: string;
  readonly hasta?: string;
  /**
   * La fecha de los conceptos de fecha manual. Es una sola y vale para todos
   * ellos: se escribe una vez en la banda. Sin ella no traen datos y no se
   * pueden tildar.
   */
  readonly rangoDesde?: string;
  readonly rangoHasta?: string;
}

export type EntradaConsulta = SeleccionPeriodo & {
  readonly operacion: string;
};

/**
 * Los campos del período, como los espera el servidor. Van planos y no
 * anidados: la descarga del Excel es un enlace y los manda por query string,
 * así que esta es la única forma que sirve para las cuatro rutas.
 */
function camposDelPeriodo(entrada: EntradaConsulta): Record<string, string> {
  return {
    ...(entrada.anio === undefined ? {} : { anio: String(entrada.anio) }),
    ...(entrada.mes === undefined ? {} : { mes: String(entrada.mes) }),
    ...(entrada.desde === undefined ? {} : { desde: entrada.desde }),
    ...(entrada.hasta === undefined ? {} : { hasta: entrada.hasta }),
    ...(entrada.rangoDesde === undefined ? {} : { rangoDesde: entrada.rangoDesde }),
    ...(entrada.rangoHasta === undefined ? {} : { rangoHasta: entrada.rangoHasta }),
  };
}

/** Tramos liquidables de la operación en el período. Es una lectura: no impacta nada. */
export function consultar(entrada: EntradaConsulta): Promise<RespuestaConsulta> {
  return postear<RespuestaConsulta>('/api/novedades/consultar', entrada);
}

/**
 * La URL de la planilla del período.
 *
 * El botón de la pantalla es un enlace a esta dirección: la descarga la
 * resuelve el navegador contra el servidor, sin pasar por JavaScript. Bajar el
 * archivo con `fetch` y un Blob parecía más prolijo, pero Edge y Chrome
 * cancelan esa descarga sin avisar y el usuario se queda con el cartel de
 * "se descargó" y ningún archivo (12-10-2026).
 *
 * En la URL viajan la operación y el período, que no son datos de nadie.
 */
export function urlConsultaXlsx(entrada: EntradaConsulta, tokenAcuse?: string): string {
  const parametros = new URLSearchParams({
    operacion: entrada.operacion,
    tiposChofer: entrada.tiposChofer.join(','),
    ...camposDelPeriodo(entrada),
  });
  // El token vuelve como cookie junto con el archivo: es lo único que le
  // permite a la pantalla saber que la descarga empezó y cerrar el cartel.
  if (tokenAcuse !== undefined) parametros.set('descarga', tokenAcuse);
  return `/api/novedades/exportar?${parametros.toString()}`;
}

export type EntradaImpacto = EntradaConsulta & {
  /** Los conceptos tildados. No se acepta un "todos" implícito. */
  readonly conceptos: readonly string[];
  /**
   * Las novedades elegidas, como pares DNI + concepto. Sin esto van todas.
   * Nunca viaja el importe: eso lo vuelve a calcular el servidor.
   */
  readonly novedades?: readonly {
    readonly dni: string;
    readonly concepto: string;
    /** DNI escrito a mano para la etapa de pruebas. */
    readonly dniVisma?: string;
  }[];
};

/**
 * Simulacro: arma el payload exacto que se enviaría a Visma y lo devuelve sin
 * postear nada. Es lo que se mira antes de impactar.
 */
export function simularImpacto(entrada: EntradaImpacto): Promise<RespuestaImpacto> {
  return postear<RespuestaImpacto>('/api/novedades/impactar', { ...entrada, simular: true });
}

/**
 * El impacto de verdad: escribe en la liquidación de Visma.
 *
 * `simular: false` va explícito, acá y del lado del servidor: escribir en un
 * sistema externo se pide, no se hereda de un cuerpo incompleto.
 */
export function impactarEnVisma(entrada: EntradaImpacto): Promise<RespuestaImpacto> {
  return postear<RespuestaImpacto>('/api/novedades/impactar', { ...entrada, simular: false });
}
